// Helpers shared by mode modules. Mode modules never import the mode registry (directly or through
// sim modules that do), so `MODES` can be loaded from any entry point without an import cycle.
import { TUNE, VOID } from '../constants';
import { BS } from '../ids';
import { MAPS } from '../maps/index';
import { hasBody } from '../sim/bean';
import { respawnBean } from '../sim/spawn';
import type { Vec2, World } from '../types';
import { dcos, dsin } from '../sim/dmath';

/** A spawn whose ground is about to go (a cracking cake tile, a doomed wedge) or sits in a hazard's path. */
const UNSAFE_DANGER = 0.5;

const pick: Vec2 = { x: 0, y: 0 };

/**
 * Where a respawning bean parachutes in (§4.3): of the map's spawns that have ground, the one
 * farthest from every bean with a body, skipping spawns the map rates dangerous over the descent
 * and the moments after landing (a cracking Crumble Cake tile, a doomed Teacup wedge, a sweeper
 * about to pass) unless no spawn is safe; then the least dangerous one. The map center when no
 * spawn has ground (the Cake's center tile survives Sudden Death). Deterministic, no RNG.
 */
export function safeRespawnPoint(w: World, out: Vec2 = pick): Vec2 {
  const map = MAPS[w.cfg.mapId];
  const ahead = TUNE.respawn.paraTicks + TUNE.bot.dangerAheadTicks;
  let best = -1;
  let bestScore = -Infinity;
  let bestDanger = Infinity;
  for (let i = 0; i < map.spawns.length; i++) {
    const s = map.spawns[i];
    if (map.groundAt(w, s.x, s.y) === VOID) continue;
    const danger = Math.min(1, map.danger(w, s.x, s.y, ahead));
    let nearest = Infinity;
    for (const b of w.beans) if (hasBody(b)) nearest = Math.min(nearest, Math.hypot(b.pos.x - s.x, b.pos.y - s.y));
    const safe = danger < UNSAFE_DANGER;
    const bestSafe = bestDanger < UNSAFE_DANGER;
    const better = safe !== bestSafe ? safe : safe ? nearest > bestScore : danger < bestDanger || (danger === bestDanger && nearest > bestScore);
    if (best < 0 || better) {
      best = i;
      bestScore = nearest;
      bestDanger = danger;
    }
  }
  const p = best >= 0 ? map.spawns[best] : map.center;
  out.x = p.x;
  out.y = p.y;
  if (best < 0 && !fits(w, p.x, p.y)) nearestFit(w, out);
  return out;
}

/** A bean's body fits on ground at (x, y): the center and four points a radius away all have ground. */
function fits(w: World, x: number, y: number): boolean {
  const map = MAPS[w.cfg.mapId];
  const r = TUNE.bean.radius;
  return map.groundAt(w, x, y) !== VOID && map.groundAt(w, x + r, y) !== VOID && map.groundAt(w, x - r, y) !== VOID
    && map.groundAt(w, x, y + r) !== VOID && map.groundAt(w, x, y - r) !== VOID;
}

/**
 * (WP4) With no spawn and not even the center left (a Crumble Cake whose middle has crumbled), the
 * nearest spot to the center a bean fits on, searched on rings; the center if there is none.
 */
function nearestFit(w: World, out: Vec2): void {
  const c = MAPS[w.cfg.mapId].center;
  for (let r = 0.5; r <= 10; r += 0.5) {
    const n = Math.max(8, Math.round(r * 8));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const x = c.x + dcos(a) * r;
      const y = c.y + dsin(a) * r;
      if (fits(w, x, y)) {
        out.x = x;
        out.y = y;
        return;
      }
    }
  }
}

/**
 * Respawning modes: counts down beans waiting in RESPAWN (from the tick after their KO) and
 * parachutes each one in at `safeRespawnPoint` when its delay ends. Server-only (respawn
 * selection, §18.2).
 */
/** With no floor anywhere to come down on, a respawn waits this many ticks and looks again. */
export const RESPAWN_RETRY_TICKS = 15;

export function tickRespawns(w: World, predict: boolean): void {
  if (predict) return;
  for (const b of w.beans) {
    if (!b.active || b.state !== BS.RESPAWN || b.respawnTicks === 0 || b.koTick === w.tick) continue;
    if (--b.respawnTicks > 0) continue;
    const p = safeRespawnPoint(w, { x: 0, y: 0 });
    // A Crumble Cake down to a regrowing center has no floor at all: stay offstage until it is back
    // (INT-A: every late Sudden Death parachute used to drop straight into the milk).
    if (MAPS[w.cfg.mapId].groundAt(w, p.x, p.y) === VOID) {
      b.respawnTicks = RESPAWN_RETRY_TICKS;
      continue;
    }
    respawnBean(w, b.slot, 'parachute', p);
  }
}
